import React,{Component} from 'react';
import {withTranslation } from 'react-i18next';
import {connect} from "react-redux";
import agreementService from "../../services/AgreementService";
import {enableAgreementSuccessToastInfo, enableAgreementRejectToastInfo} from "../../actions/ToastActions";
import {Grid, GridRow, GridColumn} from '@react/react-spectrum/Grid';
import Loader from "../Common/Loader";
import PDFModal from "../Modal/PDFModal";
import AgreementApproveModal from "../Modal/AgreementApproveModal.js";
import AgreementRejectModal from "../Modal/AgreementRejectModal.js";
import Label from '@react/react-spectrum/Label';
import Checkbox from "@react/react-spectrum/Checkbox";
import {Toast} from '@react/react-spectrum/Toast';

import {
    Cell,
    Column,
    Row,
    TableBody,
    TableHeader,
    TableView,
    Heading,
    Provider,
    defaultTheme
  } from '@adobe/react-spectrum';




export class AgreementDetails extends Component{
    constructor(props){
        super(props);
        this.state = {
            agreement: null,
            isLoading: true,
            error: null,
            agreementId: null,
            salesTranslations: {},
            pdfUrl: null,
            isApproving: false,
            termsAccepted: {
                acceptance1: false,
                acceptance2: false,
                acceptanceOff: false
            }
        };
        this.goBack = this.goBack.bind(this);
        
        // Keep track of created object URLs for cleanup
        this.createdObjectURLs = [];
        
        // Toast trigger flags to prevent duplicates
        this.toastTriggered = false;
        this.rejectToastTriggered = false;
    }

    componentDidMount() {
        
        // Extract agreement ID from query parameters
        const urlParams = new URLSearchParams(window.location.search || window.location.hash.split('?')[1]);
        const agreementId = urlParams.get('id');
        
        if(agreementId){
            this.setState({ agreementId: agreementId });
            this.loadAgreementDetails(agreementId);
        } else {
            this.setState({ 
                error: 'No agreement ID provided in URL. Please use format: /agreements?id=AGREEMENT_ID',
                isLoading: false 
            });
        }
    }

    loadAgreementDetails = (agreementId) => {
        
        this.setState({ isLoading: true, error: null });
        
        // Add timeout to prevent infinite loading
        const timeoutId = setTimeout(() => {
            this.setState({ 
                error: 'Request timeout - API call took too long',
                isLoading: false 
            });
        }, 10000);
        
        return agreementService.getAgreementById(agreementId)
            .then((response) => {
                clearTimeout(timeoutId);
                
                if (response && response.success && response.data) {
                    this.setState({ 
                        agreement: response.data,
                        isLoading: false 
                    }, () => {
                        // Load sales translations after agreement data is available
                        this.loadSalesTranslations();
                    });
                } else {
                    const errorMsg = response.message || 'No agreement data returned from API';
                    this.setState({ 
                        error: errorMsg,
                        isLoading: false 
                    });
                }
            })
            .catch((error) => {
                clearTimeout(timeoutId);
                
                // For testing purposes, provide fallback mock data
                this.setState({
                    isLoading: false,
                    error: 'Failed to load agreement details'
                });
            });
    };

    goBack = () => {
        this.props.history.push('/');
    };

    componentDidUpdate(prevProps, prevState) {
        // Reload translations if locale changed
        if (prevProps.locale !== this.props.locale) {
            this.loadSalesTranslations();
        }
        
        // Reload translations if agreement data changed (and contains language info)
        if (prevState.agreement !== this.state.agreement && this.state.agreement) {
            const prevAgreementLocale = (prevState.agreement && prevState.agreement.locale) || 
                                       (prevState.agreement && prevState.agreement.agreementLanguage);
            const currentAgreementLocale = (this.state.agreement && this.state.agreement.locale) || 
                                          (this.state.agreement && this.state.agreement.agreementLanguage);
                                          
            if (prevAgreementLocale !== currentAgreementLocale) {
                this.loadSalesTranslations();
            }
        }
    }

    componentWillUnmount() {
        // Clean up any created object URLs to prevent memory leaks
        this.createdObjectURLs.forEach(url => {
            try {
                URL.revokeObjectURL(url);
            } catch (error) {
            }
        });
        this.createdObjectURLs = [];
        
        // Reset toast flags
        this.toastTriggered = false;
        this.rejectToastTriggered = false;
    }

    // Reset toast trigger flags
    resetToastFlags = () => {
        this.toastTriggered = false;
        this.rejectToastTriggered = false;
    };

    // Handle checkbox changes for terms acceptance
    handleTermsAcceptanceChange = (checkboxKey, isChecked) => {
        
        this.setState(prevState => ({
            termsAccepted: {
                ...prevState.termsAccepted,
                [checkboxKey]: isChecked
            }
        }), () => {
        });
    };

    // Check if all required terms are accepted
    areTermsAccepted = () => {
        const { agreement, termsAccepted } = this.state;
        
        if (!agreement) return false;
        
        if (agreement.status === 'PUBLISHED' && agreement.autoRenewal) {
            // For auto-renewal agreements, both checkboxes must be checked
            return termsAccepted.acceptance1 && termsAccepted.acceptance2;
        } else if (agreement.status === 'PUBLISHED' && !agreement.autoRenewal) {
            // For non-auto-renewal agreements, single checkbox must be checked
            return termsAccepted.acceptanceOff;
        }
        
        // For non-PUBLISHED agreements, no terms acceptance required
        return true;
    };

    loadSalesTranslations = async () => {
        try {
            // Get locale from agreement data first, then props, then default to 'en'
            const { agreement } = this.state;
            const currentLocale = (agreement && agreement.agreementLanguage) || 'en';
            
            
            const salesTranslations = await agreementService.fetchLocaleFile(currentLocale, 'en');
            this.setState({ salesTranslations });
        } catch (error) {
            // Fallback to empty object
            this.setState({ salesTranslations: {} });
        }
    };

    // Helper function to get status label styling and text
    getStatusLabel = (status) => {
        const statusMap = {
            'PUBLISHED': {
                text: this.getSalesText('NEW_ORDER.labels.status.published', 'PENDING REVIEW'),
                color: '#28a745', // Green
                backgroundColor: '#d4edda',
                borderColor: '#c3e6cb'
            },
            'APPROVED': {
                text: this.getSalesText('NEW_ORDER.labels.status.approved', 'ACCEPTED'),
                color: '#155724', // Dark green
                backgroundColor: '#d1ecf1',
                borderColor: '#bee5eb'
            },
            'REJECTED': {
                text: this.getSalesText('NEW_ORDER.labels.status.rejected', 'DECLINED'),
                color: '#721c24', // Dark red
                backgroundColor: '#f8d7da',
                borderColor: '#f5c6cb'
            },
            'CLOSED': {
                text: this.getSalesText('NEW_ORDER.labels.status.closed', 'CLOSED'),
                color: '#721c24', // Dark red
                backgroundColor: '#f8d7da',
                borderColor: '#f5c6cb'
            },
            'EXPIRED': {
                text: this.getSalesText('NEW_ORDER.labels.status.expired', 'EXPIRED'),
                color: '#721c24', // Dark red
                backgroundColor: '#f8d7da',
                borderColor: '#f5c6cb'
            }
        };

        return statusMap[status] || statusMap['DRAFT'];
    };

    // Helper function to get localized text from both sales translations and regular i18n
    getSalesText = (path, fallback = '') => {
        const { salesTranslations } = this.state;
        const { t } = this.props;
        
        // First try to get from sales translations (sales-*.json)
        try {
            const keys = path.split('.');
            let value = salesTranslations;
            
            for (const key of keys) {
                if (value && typeof value === 'object' && value[key] !== undefined) {
                    value = value[key];
                } else {
                    break;
                }
            }
            
            if (typeof value === 'string') {
                return value;
            }
        } catch (error) {
        }
        
        // If not found in sales translations, try regular i18n (str-*.json)
        if (t) {
            try {
                const i18nResult = t(path, { defaultValue: null });
                if (i18nResult && i18nResult !== path) {
                    return i18nResult;
                }
            } catch (error) {
            }
        }
        
        // Return fallback if not found in either source
        return fallback;
    };

    // Handle approval start (when confirm is clicked)
    handleApprovalStart = (agreementId) => {
        
        // Set global loading state
        this.setState({ isApproving: true });
    };

    // Handle rejection start (when confirm is clicked)
    handleRejectionStart = (agreementId) => {
        
        // Set global loading state
        this.setState({ isApproving: true });
    };

    // Handle successful approval
    handleApprovalSuccess = (agreementId) => {
        
        // Show success toast (only once)
        if (!this.toastTriggered) {
            this.props.setAgreementSuccessToastFlag(true);
            this.toastTriggered = true;
        } else {
        }
        
        // Use current state agreement ID if available, otherwise use passed ID
        const idToRefresh = this.state.agreementId || agreementId;
        
        // Add small delay to allow API to update
        setTimeout(() => {
            // Refresh agreement details
            this.loadAgreementDetails(idToRefresh).then(() => {
                // Clear loading state after refresh
                this.setState({ isApproving: false });
                // Reset toast trigger flag after successful refresh
                setTimeout(() => {
                    this.toastTriggered = false;
                }, 2000); // Reset after 2 seconds
            }).catch((error) => {
                // Clear loading state even on error
                this.setState({ isApproving: false });
                this.toastTriggered = false;
            });
        }, 1000); // 1 second delay
        
        // Clear any previous errors
        this.setState({ error: null });
    };

    // Handle approval error
    handleApprovalError = (error, agreementId) => {
        this.setState({termsAccepted: {
            acceptance1: false,
            acceptance2: false,
            acceptanceOff: false
        }});
        // Clear global loading state
        this.setState({ isApproving: false });
        
        const apiMessage = error.response && error.response.data && error.response.data.message;
        const displayMessage = apiMessage || error.message || 'Unknown error';
        this.setState({ 
            error: `Failed to approve agreement: ${displayMessage}` 
        });
    };

    // Handle successful rejection
    handleRejectionSuccess = (agreementId, rejectionReason) => {
        
        // Show reject success toast (only once)
        if (!this.rejectToastTriggered) {
            this.props.setAgreementRejectToastFlag(true);
            this.rejectToastTriggered = true;
        } else {
        }
        
        // Use current state agreement ID if available, otherwise use passed ID
        const idToRefresh = this.state.agreementId || agreementId;
        
        // Add small delay to allow API to update
        setTimeout(() => {
            // Refresh agreement details
            this.loadAgreementDetails(idToRefresh).then(() => {
                // Clear loading state after refresh
                this.setState({ isApproving: false });
                // Reset toast trigger flag after successful refresh
                setTimeout(() => {
                    this.rejectToastTriggered = false;
                }, 2000); // Reset after 2 seconds
            }).catch((error) => {
                // Clear loading state even on error
                this.setState({ isApproving: false });
                this.rejectToastTriggered = false;
            });
        }, 1000); // 1 second delay
        
        // Clear any previous errors
        this.setState({ error: null });
    };

    // Handle rejection error
    handleRejectionError = (error, agreementId, rejectionReason) => {
        this.setState({termsAccepted: {
            acceptance1: false,
            acceptance2: false,
            acceptanceOff: false
        }});
        
        // Clear global loading state
        this.setState({ isApproving: false });
        
        const apiMessage = error.response && error.response.data && error.response.data.message;
        const displayMessage = apiMessage || error.message || 'Unknown error';
        this.setState({ 
            error: `Failed to decline agreement: ${displayMessage}` 
        });
    };

    // Handle PDF viewing
    viewAgreementPDF = async (agreementId,status, download = false) => {
        
        try {
            const response = await agreementService.viewAgreementPDF(agreementId,status, download);
            
            if (response && response.data) {
                // The response.data is always a blob for PDF content (stream format for both cases)
                const pdfBlob = response.data;
                
                // Create object URL from the blob
                const pdfUrl = URL.createObjectURL(pdfBlob);
                
                // Track the created URL for cleanup (only for viewing, downloads clean up immediately)
                if (!download) {
                    this.createdObjectURLs.push(pdfUrl);
                }
                
                // Extract filename from Content-Disposition header if available
                let filename = `agreement-${agreementId}.pdf`; // Default filename
                const contentDisposition = response.headers['content-disposition'];
                if (contentDisposition) {
                    const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
                    if (filenameMatch && filenameMatch[1]) {
                        filename = filenameMatch[1].replace(/['"]/g, '');
                    }
                }
                
                
                if (download) {
                    // For downloads, trigger download with proper filename
                    const link = document.createElement('a');
                    link.href = pdfUrl;
                    link.download = filename;
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    
                    // Clean up the object URL after download
                    setTimeout(() => URL.revokeObjectURL(pdfUrl), 1000);
                    
                    return;
                } else {
                    // For viewing, store the blob URL in state for the PDF viewer
                    this.setState({ pdfUrl });
                    return pdfUrl;
                }
                
            } else {
                throw new Error('Invalid PDF response - no data received');
            }
            
        } catch (error) {
            console.log('Error in viewAgreementPDF:', error);
        }
    };

    // Handle PDF downloading
    downloadAgreementPDF = async (agreementId,status) => {
        return this.viewAgreementPDF(agreementId,status, true);
    };

    formatCurrency = (amount, currency = 'USD') => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: currency
        }).format(amount);
    };

    getStatusDisplayText = (status) => {
        if (status === 'PENDING') {
            return this.getSalesText('NEW_ORDER.labels.status.PENDING', 'AWAITING APPROVAL');
        } else if (status === 'DRAFT') {
            return this.getSalesText('NEW_ORDER.labels.status.PENDING', 'AWAITING APPROVAL');
        } else if (status === 'APPROVED') {
            return this.getSalesText('NEW_ORDER.labels.status.APPROVED', 'COMPLETED');
        } else if (status === 'REJECTED') {
            return this.getSalesText('NEW_ORDER.labels.status.REJECTED', 'REJECTED');
        } else if (status) {
            return status.toUpperCase();
        } else {
            return 'UNKNOWN';
        }
    };

    getStatusBackgroundColor = (status) => {
        if (status === 'DRAFT') {
            return '#ffc107';
        } else if (status === 'APPROVED') {
            return '#28a745';
        } else if (status === 'REJECTED') {
            return '#dc3545';
        } else if (status === 'PENDING') {
            return '#ff6b35';
        } else {
            return '#6c757d';
        }
    };

    getStatusTextColor = (status) => {
        if (status === 'DRAFT') {
            return '#212529';
        } else {
            return 'white';
        }
    };

    getProductRowValue = (item, columnKey) => {
        if (columnKey === 'productDescription') {
            return item.productFamily + ' ' + item.additionalDetails;
        } else {
            return item[columnKey];
        }
    };

    getCustomerTaxType = (customerDetails) => {
        if (customerDetails && customerDetails.address && customerDetails.address.country && customerDetails.address.country.code && customerDetails.address.country.code === 'AU') {
           return <span style={{fontSize: '14px', fontWeight: '500', color: '#666'}}>({customerDetails.taxType} {customerDetails.taxId})</span>;
        } else {
            return null;
        }
    };



    render(){
        const { agreement, isLoading, error, isApproving } = this.state;
        

        if (isLoading) {
            return <Loader loaderSize="L" />;
        }

        // Show global loader during approval process
        if (isApproving) {
            return (
                <div style={{ 
                    position: 'fixed', 
                    top: 0, 
                    left: 0, 
                    width: '100%', 
                    height: '100%', 
                    backgroundColor: 'rgba(0,0,0,0.5)', 
                    display: 'flex', 
                    justifyContent: 'center', 
                    alignItems: 'center',
                    zIndex: 9999
                }}>
                    <div style={{
                        backgroundColor: 'white',
                        padding: '30px',
                        borderRadius: '8px',
                        textAlign: 'center'
                    }}>
                        <div style={{position: 'relative'}}>
                            <Loader loaderSize="M" />
                        </div>
                        <div style={{marginTop: '25px',fontSize: '16px' }}>
                            Processing approval...
                        </div>
                        
                    </div>
                </div>
            );
        }

        return(
            <div>
                {/* Header Section */}
                <section>
                    { error ? <div style={{textAlign: 'center'}}><Toast variant="error">
                    {error}</Toast></div>: ""}
                    <div className="header">
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingLeft: '20px' }}>
                            <h3> 
                                {this.getSalesText('NEW_ORDER.header.adobeSalesOrder', 'Adobe Sales Order')}: <span style={{fontWeight: '400',fontSize: '14px'}}>{agreement.agreementNumber}</span>
                            </h3>
                            <div style={{ display: 'flex', gap: '15px', justifyContent:'right', alignItems: 'center',marginRight: '20px' }}>
                                {/* Status Label */}
                                {agreement.status === 'APPROVED' && (
                                    <Label 
                                        className="status-label-element"
                                        variant="green"
                                        id="green"
                                        style={{padding: '5px 15px',textTrasform: 'uppercase'}}
                                    >
                                        {this.getStatusLabel(agreement.status).text}
                                    </Label>
                                )}
                                {agreement.status === 'PUBLISHED' && (
                                    <Label className="status-label-element"
                                        variant="or"
                                        id="orange"
                                        style={{padding: '5px 15px',textTrasform: 'uppercase'}}
                                    >
                                        {this.getStatusLabel(agreement.status).text}
                                    </Label>
                                )}
                                {agreement.status === 'REJECTED' && (
                                    <Label className="status-label-element"
                                        variant="red"
                                        id="red"
                                        style={{padding: '5px 15px',textTrasform: 'uppercase'}}
                                    >
                                        {this.getStatusLabel(agreement.status).text}
                                    </Label>
                                )}
                                {agreement.status === 'EXPIRED' && (
                                    <Label className="status-label-element"
                                        variant="red"
                                        id="red"
                                        style={{padding: '5px 15px',textTrasform: 'uppercase'}}
                                    >
                                        {this.getStatusLabel(agreement.status).text}
                                    </Label>
                                )}
                                {agreement.status === 'CLOSED' && (
                                    <Label className="status-label-element"
                                        variant="red"
                                        id="red"
                                        style={{padding: '5px 15px',textTrasform: 'uppercase'}}
                                    >
                                        {this.getStatusLabel(agreement.status).text}
                                    </Label>
                                )}
                                
                                {/* PDF Button */}
                                <PDFModal 
                                    btnLabel={this.getSalesText('NEW_ORDER.labels.details.pdfBtn.viewPdf', 'View PDF')}
                                    variant="secondary"
                                    pdfUrl={this.state.pdfUrl}
                                    agreementNumber={agreement.agreementNumber}
                                    customerName={agreement.customerName}
                                    agreementId={agreement.agreementId}
                                    onPDFClick={() => this.viewAgreementPDF(agreement.agreementId,agreement.status)}
                                    onPDFDownload={() => this.downloadAgreementPDF(agreement.agreementId)}
                                    status={agreement.status}
                                />
                            </div>
                        </div>
                    </div>
                </section>

                {/* Agreement Details */}
                <div className="agreement-content">
                    <div className="agreement-content-details" data-am-id="agreementContent" style={{padding: '0px 20px'}}>
                        {agreement.products && agreement.products.length > 0 ? (
                            <div className="products-table">
                                {/* Customer Information Section */}
                                <Grid style={{backgroundColor: '#ffffff', padding: '20px', border: '1px solid #dee2e6', marginBottom: '30px',borderRadius: '5px'}}>
                                    <GridRow>
                                        <GridColumn size={3}>
                                            <div style={{marginBottom: '10px',fontWeight: 'bold',fontSize: '18px'}}>
                                                <div style={{fontSize: '14px', color: '#333', fontWeight: 'bold'}}>{this.getSalesText('NEW_ORDER.accountDetails.customer', 'Customer')} </div>
                                                <div style={{fontSize: '14px', fontWeight: '500', color: '#666'}}>
                                                    {agreement.customerName} &nbsp;
                                                    {agreement.customerDetails && this.getCustomerTaxType(agreement.customerDetails)}
                                                </div>
                                            </div>
                                        </GridColumn>
                                        <GridColumn size={3}>
                                            <div style={{marginBottom: '10px'}}>
                                                <div style={{fontSize: '14px', color: '#333', fontWeight: 'bold'}}>{this.getSalesText('NEW_ORDER.accountDetails.vipMembershipNumber', 'VIP Membership Number')} </div>
                                                <div style={{fontSize: '14px', fontWeight: '500', color: '#666'}}>
                                                    {agreement.contractId}
                                                </div>
                                            </div>
                                        </GridColumn>
                                        <GridColumn size={3}>
                                            <div style={{marginBottom: '10px'}}>
                                                <div style={{fontSize: '14px', color: '#333', fontWeight: 'bold'}}>{this.getSalesText('NEW_ORDER.accountDetails.date', 'Date')} </div>
                                                <div style={{fontSize: '14px', fontWeight: '500', color: '#666'}}>
                                                    {agreement.agreementPublishDate ? 
                                                        new Date(agreement.agreementPublishDate + 'Z').toLocaleDateString('en-GB', {
                                                            timeZone: 'America/Los_Angeles',
                                                            day: '2-digit',
                                                            month: 'short',
                                                            year: 'numeric'
                                                        }).replace(',', '').replace(/ /g, '-').toUpperCase() : 
                                                        'N/A'}
                                                </div>
                                            </div>
                                        </GridColumn>
                                        <GridColumn size={3}>
                                            <div style={{marginBottom: '10px'}}>
                                                <div style={{fontSize: '14px', color: '#333', fontWeight: 'bold'}}>{this.getSalesText('NEW_ORDER.accountDetails.currency', 'Currency')} </div>
                                                <div style={{fontSize: '14px', fontWeight: '500', color: '#666'}}>
                                                    {agreement.currency || 'USD'}
                                                </div>
                                            </div>
                                        </GridColumn>
                                    </GridRow>
                                </Grid>
                                <Heading level={3} style={{borderBottom: '1px solid #e1e1e1', paddingBottom: '10px'}}>
                                {this.getSalesText('NEW_ORDER.labels.details.productAndServices', 'Products and Services')}
                                </Heading>
                                <div>
                                    <Provider theme={defaultTheme} colorScheme="light">                                  
                                        <TableView overflowMode="wrap">
                                            <TableHeader>
                                                <Column key="sku" allowsResizing>{this.getSalesText('NEW_ORDER.products.sku', 'SKU')}</Column>
                                                <Column key="productDescription" allowsResizing>{this.getSalesText('NEW_ORDER.products.productDescription', 'Product Description')} </Column>
                                                <Column key="billingCycle" allowsResizing>{this.getSalesText('NEW_ORDER.products.billingCycle', 'Billing Cycle')}</Column>
                                                <Column key="qty" allowsResizing>{this.getSalesText('NEW_ORDER.products.qty', 'Qty')}</Column>
                                                <Column key="unitOfMeasure" allowsResizing>{this.getSalesText('NEW_ORDER.products.uomLabel', 'Unit of Measure')}</Column>
                                                <Column key="startDate" allowsResizing>
                                                {this.getSalesText('NEW_ORDER.products.licenseTermLabel', 'License Term')}<br></br>  
                                                {this.getSalesText('NEW_ORDER.products.startDate', 'Start Date')}</Column>
                                                <Column key="endDate" allowsResizing>
                                                {this.getSalesText('NEW_ORDER.products.licenseTermLabel', 'License Term')}<br></br>
                                                {this.getSalesText('NEW_ORDER.products.endDate', 'End Date')}</Column>
                                                <Column key="annualUnitPrice" allowsResizing>{this.getSalesText('NEW_ORDER.products.annualUnitPrice', 'Annual Unit Price')}</Column>
                                                <Column key="termUnitPrice" allowsResizing>{this.getSalesText('NEW_ORDER.products.termUnitPrice', 'Term Unit Price')}</Column>
                                                <Column key="totalPrice" allowsResizing>{this.getSalesText('NEW_ORDER.products.totalFees', 'Total Fees')}</Column>
                                            </TableHeader>
                                            <TableBody
                                                items={agreement.products}
                                            >
                                                {(item) => (
                                                    <Row key={item.sku}>
                                                        {(columnKey) => <Cell>{this.getProductRowValue(item, columnKey)}</Cell>}
                                                    </Row>
                                                )}
                                            </TableBody>
                                        </TableView>
                                    </Provider>  
                                </div>                             
                                 {/* Totals Section */}
                                <Grid style={{padding: '15px 0px 0px 0px', marginTop: '0'}}>
                                    <GridRow>
                                        <GridColumn size={8}>
                                            <div style={{fontSize: '14px', color: '#888', fontWeight: 'bold'}}>
                                                *{this.getSalesText('NEW_ORDER.disclaimer', '')}
                                            </div>
                                        </GridColumn>
                                        <GridColumn size={4}>
                                            <GridColumn size={12}>
                                                <div style={{display:'flex',justifyContent:'flex-end'}}>
                                                    <div style={{fontSize: '14px', fontWeight: '600', marginBottom: '5px'}}>{this.getSalesText('NEW_ORDER.products.totals.totalFee', 'Total Fee')}: </div> &nbsp;&nbsp;
                                                    <div>
                                                        {agreement.subTotalWithoutTax}
                                                    </div>
                                                </div>
                                            </GridColumn>
                                            <GridColumn size={12}>
                                                <div style={{display:'flex',justifyContent:'flex-end'}}>
                                                    <div style={{fontSize: '14px', fontWeight: '600', marginBottom: '5px'}}>{this.getSalesText('NEW_ORDER.products.totals.estimatedTax', 'Estimated Tax')}*: </div>&nbsp;&nbsp;
                                                    <div>
                                                        {agreement.estimatedTax}
                                                    </div>
                                                </div>
                                            </GridColumn>
                                            <GridColumn size={12}>
                                                <div style={{display:'flex',justifyContent:'flex-end'}}>
                                                    <div style={{fontSize: '14px', fontWeight: 'bold', marginBottom: '5px'}}>{this.getSalesText('NEW_ORDER.products.totals.grandTotalWithTax', 'Grand Total Fee including Estimated Tax')}*: </div> &nbsp;&nbsp;
                                                    <div>
                                                    {agreement.grandTotal}
                                                    </div>
                                                </div>
                                            </GridColumn>
                                        </GridColumn>
                                    </GridRow>
                                </Grid>

                                {/* Terms and Conditions Section */}
                                {agreement.terms && agreement.terms.length > 0 && (
                                    <div>
                                        <Heading level={3} style={{borderBottom: '1px solid #e1e1e1', paddingBottom: '10px'}}>
                                        {this.getSalesText('NEW_ORDER.labels.details.termsAndConditions', 'Sales Order Terms and Conditions')}:
                                        </Heading>
                                        <Grid style={{padding: '20px',border: '1px solid #dee2e6',borderRadius: '5px',backgroundColor: '#fff'}}>
                                            <GridRow>
                                                <GridColumn size={12}>
                                                    <div style={{lineHeight: '1.6'}}>
                                                        {agreement.terms
                                                            .sort((a, b) => (a.sequence || 0) - (b.sequence || 0))
                                                            .map((term, index) => (
                                                            <div key={index} style={{marginBottom: '20px', padding: '0px', display: 'flex', alignItems: 'flex-start'}}>
                                                                {/* Term Number */}
                                                                <div style={{
                                                                    fontSize: '16px',
                                                                    fontWeight: 'bold',
                                                                    marginRight: '15px',
                                                                    minWidth: '15px'
                                                                }}>
                                                                    {index + 1}.
                                                                </div>
                                                                
                                                                {/* Term Content */}
                                                                <div style={{
                                                                    fontSize: '14px',
                                                                    color: '#333',
                                                                    textAlign: 'justify',
                                                                    flex: 1
                                                                }}>
                                                                    {/* Display localized text if available, otherwise show termKey */}
                                                                    {term.localizedText ? (
                                                                        <div dangerouslySetInnerHTML={{ 
                                                                            __html: term.localizedText.replace(/{{date}}/g, (() => {
                                                                                // Use date from term.values if available, otherwise use current date
                                                                                const dateToUse = (term.values && term.values.date) 
                                                                                    ? term.values.date 
                                                                                    : new Date();
                                                                                return dateToUse;
                                                                            })())
                                                                        }} />
                                                                    ) : (
                                                                        <span style={{color: '#666', fontStyle: 'italic'}}>
                                                                            Term Key: {term.termKey || `Term ${index + 1}`}
                                                                        </span>
                                                                    )}
                                                                    
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </GridColumn>
                                            </GridRow>
                                        </Grid>
                                    </div>
                                )}

                                {
                                    agreement.status === 'PUBLISHED' && agreement.autoRenewal && (
                                        <div style={{padding: '20px',border: '1px solid #dee2e6',borderRadius: '5px',backgroundColor: '#fff',marginTop: '20px'}}>
                                            <div style={{display: 'flex', alignItems: 'flex-start',marginBottom: '10px'}}>
                                                <Checkbox
                                                    isSelected={this.state.termsAccepted.acceptance1}
                                                    onChange={(isChecked) => this.handleTermsAcceptanceChange('acceptance1', isChecked)}
                                                />
                                                <div dangerouslySetInnerHTML={{ __html: this.getSalesText('NEW_ORDER.termAcceptanceRenewalOn.acceptanceText1')}} />
                                            </div>
                                            <div style={{display: 'flex', alignItems: 'flex-start'}}>
                                                <Checkbox
                                                    isSelected={this.state.termsAccepted.acceptance2}
                                                    onChange={(isChecked) => this.handleTermsAcceptanceChange('acceptance2', isChecked)}
                                                />
                                                    <div dangerouslySetInnerHTML={{ __html: this.getSalesText('NEW_ORDER.termAcceptanceRenewalOn.acceptanceText2')}} />
                                            </div>
                                        </div>
                                    )
                                }
                                {
                                    agreement.status === 'PUBLISHED' && !agreement.autoRenewal && (
                                        <div style={{display: 'flex', alignItems: 'flex-start',padding: '20px',border: '1px solid #dee2e6',borderRadius: '5px',backgroundColor: '#fff',marginTop: '20px'}}>
                                            <Checkbox
                                                isSelected={this.state.termsAccepted.acceptanceOff}
                                                onChange={(isChecked) => this.handleTermsAcceptanceChange('acceptanceOff', isChecked)}
                                            />
                                            <div dangerouslySetInnerHTML={{ __html: this.getSalesText('NEW_ORDER.termAcceptanceRenewalOff.acceptanceText')}} />
                                        </div>
                                    )
                                }
                                {
                                    agreement.status === 'APPROVED' && !agreement.autoRenewal && (
                                        <div>
                                        <div style={{display: 'flex', alignItems: 'flex-start',padding: '20px',border: '1px solid #dee2e6',borderRadius: '5px',backgroundColor: '#fff',marginTop: '20px'}}>
                                            <Checkbox checked disabled/>
                                            <div dangerouslySetInnerHTML={{ __html: this.getSalesText('NEW_ORDER.termAcceptanceRenewalOff.acceptanceText')}} />
                                        </div>
                                            <h3>
                                                {this.getSalesText('NEW_ORDER.approvedBy', 'Approved By')}:&nbsp;
                                                {agreement.approverDetails && agreement.approverDetails.firstName && agreement.approverDetails.lastName && (
                                                    <span>
                                                        <span>
                                                            {agreement.approverDetails.firstName} {agreement.approverDetails.lastName}
                                                        </span> &nbsp;
                                                        {agreement.approvedDate && (
                                                        <span>
                                                            {agreement.approvedDate}
                                                        </span>
                                                        )}
                                                   </span>
                                                )}
                                            </h3>
                                        
                                        </div>
                                    )
                                }
                                {
                                    agreement.status === 'APPROVED' && agreement.autoRenewal && (
                                        <div>
                                            <div style={{padding: '20px',border: '1px solid #dee2e6',borderRadius: '5px',backgroundColor: '#fff',marginTop: '20px'}}>
                                                <div style={{display: 'flex', alignItems: 'flex-start',marginBottom: '10px'}}>
                                                    <Checkbox checked disabled/>
                                                    <div dangerouslySetInnerHTML={{ __html: this.getSalesText('NEW_ORDER.termAcceptanceRenewalOn.acceptanceText1')}} />
                                                </div>
                                                <div style={{display: 'flex', alignItems: 'flex-start'}}>
                                                    <Checkbox checked disabled/>
                                                    <div dangerouslySetInnerHTML={{ __html: this.getSalesText('NEW_ORDER.termAcceptanceRenewalOn.acceptanceText2')}} />
                                                </div>
                                            </div>
                                            <h3>
                                                {this.getSalesText('NEW_ORDER.approvedBy', 'Accepted By')}:&nbsp;
                                                {agreement.approverDetails && agreement.approverDetails.firstName && agreement.approverDetails.lastName && (
                                                    <span>
                                                        <span>
                                                            {agreement.approverDetails.firstName} {agreement.approverDetails.lastName}
                                                        </span> &nbsp;&nbsp;
                                                        {agreement.approvedDate && (
                                                        <span>
                                                            {agreement.approvedDate}
                                                        </span>
                                                        )}
                                                    </span>
                                                )}
                                            </h3>
                                        </div>
                                    )
                                }
                                

                                {/* Action Buttons */}
                                {
                                    agreement.status === 'PUBLISHED' && (
                                        <Grid style={{marginTop: '20px', borderTop: '1px solid #e0e0e0', paddingTop: '15px'}}>
                                        <GridRow>
                                            <GridColumn size={12}>
                                                <div className="action-panel" style={{textAlign: 'center'}}>
                                                    <AgreementApproveModal
                                                        btnLabel={this.getSalesText('NEW_ORDER.labels.details.acceptAgreement', 'Accept Agreement')}
                                                        variant="cta"
                                                        agreementId={agreement && agreement.agreementId}
                                                        customerName={agreement && agreement.customerName}
                                                        totalValue={(agreement && agreement.totalValue) || (agreement && agreement.grandTotal)}
                                                        currency={agreement && agreement.currency}
                                                        getSalesText={this.getSalesText}
                                                        onStart={this.handleApprovalStart}
                                                        onSuccess={this.handleApprovalSuccess}
                                                        onError={this.handleApprovalError}
                                                        disabled={!this.areTermsAccepted()}
                                                    />
                                                    <AgreementRejectModal
                                                        btnLabel={this.getSalesText('NEW_ORDER.labels.details.declineAgreement', 'Decline Agreement')}
                                                        variant="negative"
                                                        agreementId={agreement && agreement.agreementId}
                                                        customerName={agreement && agreement.customerName}
                                                        totalValue={(agreement && agreement.totalValue) || (agreement && agreement.grandTotal)}
                                                        currency={agreement && agreement.currency}
                                                        getSalesText={this.getSalesText}
                                                        onStart={this.handleRejectionStart}
                                                        onSuccess={this.handleRejectionSuccess}
                                                        onError={this.handleRejectionError}
                                                        disabled={this.areTermsAccepted()}
                                                    />
                                                </div>
                                            </GridColumn>
                                        </GridRow>
                                    </Grid>
                                    )
                                }
                            </div>
                        ) : (
                            <div className="no-products" style={{textAlign: 'center', padding: '40px'}}>
                                <p>{this.getSalesText('NEW_ORDER.labels.details.noProductDetails', 'No product details available for this agreement.')}</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        )
    }
}

const mapStateToProps = (state)=>{
    return{
        workFlowId:state.ApprovalListReducer.selectedTab,
        apiStatus: state.ApprovalListReducer.apiStatus,
        locale:state.LocaleReducer.value,
        approvalList : state.ApprovalListReducer.approvalList,
    }
};

const mapDispatchToProps = (dispatch)=>{
    return{
        setAgreementSuccessToastFlag:(flag)=>{dispatch(enableAgreementSuccessToastInfo(flag))},
        setAgreementRejectToastFlag:(flag)=>{dispatch(enableAgreementRejectToastInfo(flag))}
    }
};


export default connect(mapStateToProps,mapDispatchToProps)(withTranslation()(AgreementDetails));



